import React, { useContext, useEffect, useRef } from 'react';
import { Box, Typography, Button, Paper, Avatar, CircularProgress, Fade } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AuthContext from "./auth/Context";

/**
 * Màn chắn khi nội dung yêu cầu đăng nhập.
 *
 * Bản cũ chỉ có MỘT trạng thái "Yêu cầu đăng nhập" và chỉ dựa vào `userInfo` để quyết định hiện hay
 * không. Hệ quả: trong lúc form đăng nhập / hộp chọn công ty đang mở, nền phía sau vẫn là tấm biển
 * "Yêu cầu đăng nhập" — người dùng vừa gõ mật khẩu xong vẫn thấy nó, rất khó chịu.
 *
 * Nay màn này có 3 trạng thái, chọn theo PHA XÁC THỰC do Provider cung cấp:
 *   loading      đang khởi động / đang đăng nhập / đang chọn công ty  -> chỉ hiện vòng quay, KHÔNG hiện ổ khoá
 *   choose_app   đã đăng nhập nhưng chưa chọn công ty                  -> mời CHỌN CÔNG TY (không phải mời đăng nhập)
 *   login        chưa đăng nhập                                        -> mời đăng nhập
 */
export default function LockScreen({
    autoSignin = false,
    canCancel = true,
    variant,                 // ép trạng thái (tuỳ chọn); bỏ trống -> tự suy từ pha xác thực
    message,
}) {
    const context = useContext(AuthContext);
    const hasTriggered = useRef(false);

    const AUTH_PHASE = context.AUTH_PHASE || {};
    const phase = context.authPhase;
    const busy = context.authBusy;
    const isLoggedIn = context.isLoggedIn ? context.isLoggedIn()
        : !!(context.userInfo && context.userInfo.email !== 'public');
    const needsApp = context.needsApp ? context.needsApp() : (isLoggedIn && !context.appInfo);

    const state = variant || (busy ? 'loading' : (needsApp ? 'choose_app' : (isLoggedIn ? 'loading' : 'login')));

    useEffect(() => {
        // Tự mở form đăng nhập. KHÔNG mở khi đang có luồng xác thực chạy dở (busy) — Provider đã gộp
        // lời gọi nên cũng không mở trùng, nhưng chặn sớm ở đây thì đỡ một nhịp nhấp nháy.
        if (autoSignin && !hasTriggered.current && !isLoggedIn && !busy) {
            hasTriggered.current = true;
            const timer = setTimeout(() => {
                if (typeof context.signin === 'function') {
                    context.signin(null, { allow_cancel: canCancel }).catch(() => {});
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [autoSignin, canCancel, isLoggedIn, busy]);

    // ── Đang xử lý: chỉ một vòng quay nhẹ, không có thông điệp gây hiểu nhầm ────────────────────
    if (state === 'loading') {
        const label = message || (
            phase === AUTH_PHASE.CHOOSING_APP ? 'Đang mở danh sách công ty...' :
            phase === AUTH_PHASE.LOADING ? 'Đang tải dữ liệu công ty...' :
            phase === AUTH_PHASE.SIGNING_IN ? 'Đang đăng nhập...' : 'Đang tải...'
        );
        return (
            <Fade in timeout={200}>
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={34} thickness={4} />
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                </Box>
            </Fade>
        );
    }

    // ── Đã đăng nhập, còn thiếu công ty ─────────────────────────────────────────────────────────
    if (state === 'choose_app') {
        return (
            <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, textAlign: 'center', maxWidth: 400, bgcolor: 'white', boxShadow: '0 8px 32px rgba(0,0,0,0.05)' }}>
                    <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <BusinessOutlinedIcon fontSize="large" />
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#1b5e20' }}>
                        Chọn công ty
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                        {message || `Xin chào ${context.userInfo?.name || context.userInfo?.login || context.userInfo?.email || ''}. Hãy chọn công ty bạn muốn làm việc để tiếp tục.`}
                    </Typography>
                    <Button
                        variant="contained" size="large" fullWidth color="success"
                        onClick={() => {
                            if (context.chooseAppAgain) context.chooseAppAgain().catch(() => {});
                            else if (context.signin) context.signin(null, { allow_cancel: canCancel }).catch(() => {});
                        }}
                    >
                        Chọn công ty
                    </Button>
                    <Button
                        variant="text" size="small" sx={{ mt: 1.5, color: 'text.secondary' }}
                        onClick={() => context.signout && context.signout()}
                    >
                        Đăng xuất
                    </Button>
                </Paper>
            </Box>
        );
    }

    // ── Chưa đăng nhập ──────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, textAlign: 'center', maxWidth: 400, bgcolor: 'white', boxShadow: '0 8px 32px rgba(0,0,0,0.05)' }}>
                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                    <LockOutlinedIcon fontSize="large" />
                </Avatar>

                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#1a237e' }}>
                    Yêu cầu đăng nhập
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                    {message || 'Bạn cần đăng nhập bằng tài khoản nội bộ hoặc đối tác để xem dữ liệu này.'}
                </Typography>

                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => context.signin(null, { allow_cancel: canCancel }).catch(() => {})}
                    color="primary"
                >
                    Đăng nhập ngay
                </Button>
            </Paper>
        </Box>
    );
}
